/* eslint-disable ember/no-controller-access-in-routes, warp-drive/no-legacy-request-patterns */
import Route from '@ember/routing/route';
import { service } from '@ember/service';

export default class StudentRoute extends Route {
  // Services
  @service
  session;

  @service
  store;

  @service
  studentReportActions;

  async model(params) {
    let person =
      this.store.peekRecord('person', params.person_id) ||
      (await this.store.findRecord('person', params.person_id));

    let studentReports = await this.store.query('reports/student-report', {
      studentId: params.person_id,
      schoolId: this.session.activeSchool,
    });

    // Resolve each unique template referenced by the student's reports to
    // its current version. Build oldId → currentId map for the controller.
    // A failed lookup maps to null, not to the old id: the old version's
    // deleteTime says nothing about the newest one (deletion stamps only the
    // terminal), and the controller needs to tell "unknown" from "live".
    let templateIds = new Set();
    studentReports.forEach((r) => {
      let id = r.belongsTo('reportTemplateId').id();
      if (id) templateIds.add(id);
    });
    let templateVersionMap = {};
    await Promise.all(
      Array.from(templateIds).map((id) =>
        this.studentReportActions
          .resolveCurrentTemplate(id)
          .then((current) => {
            templateVersionMap[id] = current.get('id');
          })
          .catch(() => {
            templateVersionMap[id] = null;
          }),
      ),
    );

    return { person, studentReports, templateVersionMap };
  }

  setupController(controller, model) {
    super.setupController(...arguments);
    controller.set('person', model.person);
    controller.set('studentReports', model.studentReports);
    controller.set('templateVersionMap', model.templateVersionMap);

    // The Active/Historic split asks the counts response which period each
    // template is currently in — the backend resolves that against the
    // school's rollover, and nothing here recomputes it. Landing straight on a
    // student URL never passes through the group index, which is what normally
    // loads the counts, so without this every report falls to Historic.
    //
    // Here rather than in model(): loadGroupReports reads selectedGroup, which
    // reporting.group's setupController sets — and that runs before this one,
    // but after this route's model hook.
    //
    // Only when nothing is loaded yet, so arriving from the group page doesn't
    // repeat a request it just made. Not awaited: the controller's computeds
    // depend on templateDraftInfo and re-run when it lands, so this fills the
    // split in rather than holding up the student's reports.
    let groupCtrl = this.controllerFor('reporting.group');
    let drafts = groupCtrl.templateDraftInfo;
    if (!drafts || !Object.keys(drafts).length) {
      groupCtrl.loadGroupReports().catch(() => {
        // loadGroupReports toasts its own failure; the split stays empty
        // rather than guessing at a period.
      });
    }
  }

  resetController(controller, isExiting) {
    super.resetController(...arguments);
    if (isExiting) {
      controller.set('person', null);
      controller.set('studentReports', null);
      controller.set('templateVersionMap', null);
      // Counts refresh via reporting.group.index's routeDidChange hook.
    }
  }
}
